// How an item came into existence. Sent by clients as
// `quickCaptureSource` on POST /api/item and persisted on the item as
// `captureSource` (eyes-free capture slice A).
//
// Every source listed here is counted against the Phase 2 free-tier cap;
// see assertCaptureCapAllowed. 'eyesFree' is counted because it is an AI
// capture that skipped review, not a different kind of quota, and the
// Phase 4 external sources are counted because a capture is a capture
// whichever door it came through (Q1/Q2, docs/PHASE_4_OPEN_API_MCP.md).
export const CAPTURE_SOURCE = {
    /** Quick Capture manual mode — user typed a title. */
    manual: 'manual',
    /** Quick Capture AI mode — dictated, planned, and reviewed before commit. */
    ai: 'ai',
    /** Inline add on a pane row or mobile screen. */
    inline: 'inline',
    /**
     * Dictated and committed WITHOUT the user seeing the plan. The only
     * source that starts life unreviewed, and the only one that stores a
     * transcript. Surfaced afterwards in the capture review strip.
     */
    eyesFree: 'eyesFree',
    /**
     * Phase 4 — arrived from Slack (`/tidee`, an `@tidee` mention, or the
     * message shortcut). Counted: Slack is a Free-tier feature precisely
     * because its captures count. Free plus uncounted would be an
     * unlimited capture path around the 50.
     */
    slack: 'slack',
    /**
     * Phase 4 — arrived through the MCP server or the public API. Counted:
     * an MCP `capture` runs the same planner at the same cost as an in-app
     * AI capture, and a literal `create_item` is still an item the user now
     * has. Exempting either would make the cap bypassable by anyone willing
     * to install a config file.
     */
    mcp: 'mcp',
};
/**
 * Sources that count toward the Phase 2 free-tier capture cap.
 *
 * Currently every known source. Kept as a separate list rather than
 * derived from CAPTURE_SOURCE so that exempting one later is a visible,
 * deliberate edit here — not something that falls out of adding an enum
 * member and forgetting the consequence.
 */
export const COUNTED_CAPTURE_SOURCES = [
    CAPTURE_SOURCE.manual,
    CAPTURE_SOURCE.ai,
    CAPTURE_SOURCE.inline,
    CAPTURE_SOURCE.eyesFree,
    CAPTURE_SOURCE.slack,
    CAPTURE_SOURCE.mcp,
];
const KNOWN_CAPTURE_SOURCES = new Set(Object.values(CAPTURE_SOURCE));
/**
 * Is this one of the sources we know about?
 *
 * Matters because an UNRECOGNISED source is not counted against the cap
 * — isCountedCapture asks whether the source is in COUNTED_CAPTURE_SOURCES
 * and a value that is in no list answers "no". Anywhere a source arrives
 * from outside the server, it has to be checked against this before it
 * reaches cap enforcement, or a typo (or a crafted request) buys free
 * captures. Shared from config so client and server agree on the set.
 *
 * Note this says nothing about an ABSENT source: creates with no source
 * at all are legitimate non-capture paths (drag-drop, box-settings add)
 * and are uncounted by design.
 */
export const isKnownCaptureSource = (source) => typeof source === 'string' && KNOWN_CAPTURE_SOURCES.has(source);
/**
 * Phase 2 free-tier cap — the number of lifetime captures a Free user
 * gets. Lives here rather than server-side only so the client can warn
 * before an eyes-free capture, where letting someone dictate a whole
 * sentence that is certain to be rejected wastes their words.
 *
 * The server remains the enforcer: this constant informs the UI, it does
 * not decide. See docs/PHASE_2_PAID_TIER.md.
 */
export const FREE_TIER_CAPTURE_LIMIT = 50;
//# sourceMappingURL=capture-source.js.map